import { createContext, useContext, useEffect, useReducer, type ReactNode } from 'react'

// Integrazione con la Cookie Solution di Iubenda. Sostituisce @mep-agency/next-iubenda
// (pacchetto alpha legato a Next.js) mantenendone il comportamento: i contenuti dietro
// <ConsentGate> compaiono solo quando l'utente ha dato il consenso alle finalità richieste.

export type Purpose = 'necessary' | 'functionality' | 'experience' | 'measurement' | 'marketing'

// Corrispondenza tra le finalità e gli id usati da Iubenda ("purposes" 1..5)
const PURPOSE_IDS: Record<Purpose, string> = {
  necessary: '1',
  functionality: '2',
  experience: '3',
  measurement: '4',
  marketing: '5',
}

const SCRIPT_ID = 'iubenda-cs'
const SCRIPT_SRC = 'https://cdn.iubenda.com/cs/iubenda_cs.js'

type Purposes = Record<Purpose, boolean>

interface State {
  /** true dopo la prima risposta di Iubenda (preferenze lette o consenso non necessario) */
  loaded: boolean
  purposes: Purposes
}

type Action = { type: 'update'; purposes?: Record<string, boolean> } | { type: 'not_needed' }

const setAll = (value: boolean) =>
  Object.fromEntries(Object.keys(PURPOSE_IDS).map((p) => [p, value])) as Purposes

const initialState: State = { loaded: false, purposes: setAll(false) }

function reducer(_state: State, action: Action): State {
  if (action.type === 'not_needed') {
    // Se il consenso non è richiesto (es. fuori dall'ambito GDPR) tutto è abilitato
    return { loaded: true, purposes: setAll(true) }
  }
  const received = action.purposes ?? {}
  const purposes = Object.fromEntries(
    Object.entries(PURPOSE_IDS).map(([purpose, id]) => [purpose, received[id] === true])
  ) as Purposes
  return { loaded: true, purposes }
}

const IubendaContext = createContext<State>(initialState)

export function IubendaProvider({
  config,
  children,
}: {
  config: Record<string, unknown>
  children: ReactNode
}) {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    window._iub = window._iub || []
    window._iub.csConfiguration = {
      ...config,
      callback: {
        onPreferenceExpressedOrNotNeeded: (preferences?: { purposes?: Record<string, boolean> }) =>
          dispatch(
            preferences
              ? { type: 'update', purposes: preferences.purposes }
              : { type: 'not_needed' }
          ),
      },
    }

    // La configurazione deve esistere prima che lo script parta; l'id evita doppie
    // iniezioni (StrictMode in sviluppo esegue l'effetto due volte).
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script')
      script.id = SCRIPT_ID
      script.src = SCRIPT_SRC
      script.async = true
      document.head.appendChild(script)
    }
  }, [config])

  return <IubendaContext.Provider value={state}>{children}</IubendaContext.Provider>
}

/** Mostra i figli solo se l'utente ha acconsentito a tutte le finalità indicate. */
export function ConsentGate({ purposes, children }: { purposes: Purpose[]; children: ReactNode }) {
  const state = useContext(IubendaContext)
  const granted = state.loaded && purposes.every((purpose) => state.purposes[purpose])
  return granted ? <>{children}</> : null
}
